'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Download, ExternalLink, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { runtimeTarget } from '@/lib/runtime-target'
import {
  applyDesktopComponentUpdate,
  checkDesktopUpdate,
  DesktopUpdateCheckResult,
  DesktopUpdateComponent,
  isDesktopBridgeAvailable,
  parseDesktopBridgeError,
  restartDesktopBackend,
  systemOpen,
} from '@/lib/desktop/bridge'

const updateEndpoint = process.env.NEXT_PUBLIC_DESKTOP_UPDATE_URL
const updateAppCode = process.env.NEXT_PUBLIC_DESKTOP_UPDATE_APP_CODE || 'fishown-agent-desktop'
const updateChannel = process.env.NEXT_PUBLIC_DESKTOP_UPDATE_CHANNEL || 'stable'

function getUpdatableComponents(update: DesktopUpdateCheckResult | null): DesktopUpdateComponent[] {
  return (update?.components || []).filter(
    component => component.needUpdate && component.manifestUrl,
  )
}

export function DesktopUpdateGuard() {
  const [update, setUpdate] = useState<DesktopUpdateCheckResult | null>(null)
  const [isApplying, setIsApplying] = useState(false)
  const [applyMessage, setApplyMessage] = useState('')
  const [applyProgress, setApplyProgress] = useState(0)

  const updatableComponents = useMemo(() => getUpdatableComponents(update), [update])
  const shellUpdateUrl = update?.shell?.patchUrl || update?.shell?.fullPackageUrl || null
  const shouldBlock = Boolean(update?.forceUpdate && update?.hasUpdate)

  const openShellUpdate = useCallback(async () => {
    if (shellUpdateUrl) {
      await systemOpen(shellUpdateUrl)
    }
  }, [shellUpdateUrl])

  const applyRuntimeUpdates = useCallback(async (targetUpdate: DesktopUpdateCheckResult | null) => {
    const targetComponents = getUpdatableComponents(targetUpdate)
    const targetShellUpdateUrl = targetUpdate?.shell?.patchUrl || targetUpdate?.shell?.fullPackageUrl || null

    if (!targetComponents.length) {
      if (targetShellUpdateUrl) {
        await systemOpen(targetShellUpdateUrl)
      }
      return
    }

    setIsApplying(true)
    setApplyProgress(0)
    try {
      for (let index = 0; index < targetComponents.length; index += 1) {
        const component = targetComponents[index]
        setApplyMessage(`正在更新 ${component.name}...`)
        await applyDesktopComponentUpdate(component.manifestUrl as string)
        setApplyProgress(Math.round(((index + 1) / targetComponents.length) * 100))
      }
      setApplyMessage('正在重启本地后端...')
      await restartDesktopBackend()
      setApplyMessage('更新完成')
      toast.success('桌面资源已更新')
      setUpdate(prev => prev ? { ...prev, hasUpdate: Boolean(prev.shell?.needUpdate), components: [] } : prev)
    } catch (error) {
      const parsed = parseDesktopBridgeError(error)
      setApplyMessage(parsed.message)
      toast.error('桌面资源更新失败', {
        description: parsed.message,
      })
    } finally {
      setIsApplying(false)
    }
  }, [])

  useEffect(() => {
    if (runtimeTarget !== 'desktop' || !updateEndpoint || !isDesktopBridgeAvailable()) {
      return
    }

    let cancelled = false
    const run = async () => {
      try {
        const nextUpdate = await checkDesktopUpdate(updateEndpoint, updateAppCode, updateChannel)
        if (cancelled || !nextUpdate.hasUpdate) {
          return
        }
        setUpdate(nextUpdate)

        const latestVersion = nextUpdate.latestVersion || nextUpdate.shell?.targetVersion || 'latest'
        const dismissKey = `desktop_update_dismissed_${latestVersion}`
        if (!nextUpdate.forceUpdate && !sessionStorage.getItem(dismissKey)) {
          toast.info(`发现桌面端更新 ${latestVersion}`, {
            description: nextUpdate.releaseNotes || '可以继续使用，稍后完成更新。',
            duration: Infinity,
            closeButton: true,
            action: {
              label: '更新',
              onClick: () => {
                void applyRuntimeUpdates(nextUpdate)
                const nextShellUpdateUrl = nextUpdate.shell?.patchUrl || nextUpdate.shell?.fullPackageUrl
                if (nextUpdate.shell?.needUpdate && nextShellUpdateUrl) {
                  void systemOpen(nextShellUpdateUrl)
                }
              },
            },
            onDismiss: () => {
              sessionStorage.setItem(dismissKey, 'true')
            },
          })
        }
      } catch (error) {
        console.warn('[DesktopUpdate] check failed:', error)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [applyRuntimeUpdates])

  if (!shouldBlock || !update) {
    return null
  }

  const visibleUpdate = update

  return (
    <AlertDialog open>
      <AlertDialogContent className="sm:max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>需要更新桌面端</AlertDialogTitle>
          <AlertDialogDescription>
            当前版本低于后台要求的最低版本，请完成更新后继续使用。
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogBody className="space-y-4">
          <div className="rounded-md border p-3 text-sm">
            <div className="font-medium">目标版本：{visibleUpdate.latestVersion || visibleUpdate.shell?.targetVersion || '-'}</div>
            <div className="mt-1 text-muted-foreground">最低版本：{visibleUpdate.minVersion || visibleUpdate.shell?.minVersion || '-'}</div>
            {visibleUpdate.releaseNotes ? (
              <div className="mt-2 whitespace-pre-wrap text-muted-foreground">{visibleUpdate.releaseNotes}</div>
            ) : null}
          </div>

          {updatableComponents.length ? (
            <div className="space-y-2">
              {updatableComponents.map(component => (
                <div key={component.name} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                  <span>{component.name}</span>
                  <span className="text-muted-foreground">
                    {component.currentVersion || '-'} → {component.targetVersion || '-'}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {isApplying || applyMessage ? (
            <div className="space-y-2">
              <Progress value={applyProgress} />
              <div className="text-sm text-muted-foreground">{applyMessage}</div>
            </div>
          ) : null}
        </AlertDialogBody>
        <AlertDialogFooter>
          {shellUpdateUrl ? (
            <Button variant="outline" onClick={openShellUpdate} disabled={isApplying}>
              <ExternalLink className="h-4 w-4" />
              下载安装包
            </Button>
          ) : null}
          <Button onClick={() => applyRuntimeUpdates(visibleUpdate)} disabled={isApplying || (!updatableComponents.length && !shellUpdateUrl)}>
            {isApplying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            立即更新
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
